/**
 * Connection Slice
 *
 * Manages WebSocket connection status and subscription state.
 * Tracks per-entity subscription health including reconnections,
 * stops, event counts, and timing for diagnosing connection issues.
 */
import type { StateCreator } from 'zustand';

import type { DataPlaneStore } from '../types';

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting';

export interface SubscriptionState {
  entityType: string;
  status: 'active' | 'inactive' | 'error' | 'stopped';
  lastUpdate?: Date;
  error?: string;
  /** When this subscription first connected */
  connectedAt?: Date;
  /** Total events received on this subscription */
  eventCount: number;
  /** Number of times the subscription reconnected (onStarted after error/stop) */
  reconnections: number;
  /** Number of times the subscription stopped (onStopped fired) */
  stoppedCount: number;
  /** Timestamp of most recent reconnection */
  lastReconnectedAt?: Date;
  /** Timestamp of most recent stop */
  lastStoppedAt?: Date;
}

export interface ConnectionError {
  id: string;
  timestamp: Date;
  source: 'query' | 'mutation' | 'subscription' | 'websocket';
  path?: string;
  code?: string;
  message: string;
  httpStatus?: number;
}

const MAX_RECENT_ERRORS = 50;
let errorIdCounter = 0;

export interface ConnectionSlice {
  connection: {
    status: ConnectionStatus;
    subscriptions: Map<string, SubscriptionState>;
    recentErrors: ConnectionError[];
    lastConnected?: Date;
    lastDisconnected?: Date;
  };
  setConnectionStatus: (status: ConnectionStatus) => void;
  addSubscription: (entityType: string, state?: Partial<SubscriptionState>) => void;
  updateSubscription: (entityType: string, updates: Partial<SubscriptionState>) => void;
  /** Record a subscription event (increments eventCount, updates lastUpdate) */
  recordSubscriptionEvent: (entityType: string) => void;
  /** Record a reconnection (increments reconnections, resets connectedAt) */
  recordSubscriptionReconnection: (entityType: string) => void;
  /** Record a stop (increments stoppedCount, sets status to stopped) */
  recordSubscriptionStop: (entityType: string) => void;
  removeSubscription: (entityType: string) => void;
  clearSubscriptions: () => void;
  /** Record a connection-related error (auth failures, network errors, etc.) */
  addConnectionError: (error: Omit<ConnectionError, 'id' | 'timestamp'>) => void;
  /** Clear all recorded errors */
  clearConnectionErrors: () => void;
}

export const createConnectionSlice: StateCreator<DataPlaneStore, [], [], ConnectionSlice> = (
  set,
) => ({
  connection: {
    status: 'disconnected',
    subscriptions: new Map(),
    recentErrors: [],
  },

  setConnectionStatus: (status) =>
    set((state) => ({
      connection: {
        ...state.connection,
        status,
        ...(status === 'connected'
          ? { lastConnected: new Date() }
          : status === 'disconnected'
            ? { lastDisconnected: new Date() }
            : {}),
      },
    })),

  addSubscription: (entityType, initial) =>
    set((state) => {
      const newSubscriptions = new Map(state.connection.subscriptions);
      const existing = newSubscriptions.get(entityType);
      newSubscriptions.set(entityType, {
        entityType,
        status: 'inactive',
        eventCount: existing?.eventCount ?? 0,
        reconnections: existing?.reconnections ?? 0,
        stoppedCount: existing?.stoppedCount ?? 0,
        connectedAt: existing?.connectedAt,
        lastReconnectedAt: existing?.lastReconnectedAt,
        lastStoppedAt: existing?.lastStoppedAt,
        ...initial,
      });
      return {
        connection: {
          ...state.connection,
          subscriptions: newSubscriptions,
        },
      };
    }),

  updateSubscription: (entityType, updates) =>
    set((state) => {
      const newSubscriptions = new Map(state.connection.subscriptions);
      const existing = newSubscriptions.get(entityType);
      if (existing) {
        const merged = { ...existing, ...updates };
        // Auto-set connectedAt on first transition to active
        if (updates.status === 'active' && !existing.connectedAt) {
          merged.connectedAt = new Date();
        }
        newSubscriptions.set(entityType, merged);
      }
      return {
        connection: {
          ...state.connection,
          subscriptions: newSubscriptions,
        },
      };
    }),

  recordSubscriptionEvent: (entityType) =>
    set((state) => {
      const newSubscriptions = new Map(state.connection.subscriptions);
      const existing = newSubscriptions.get(entityType);
      if (existing) {
        newSubscriptions.set(entityType, {
          ...existing,
          eventCount: existing.eventCount + 1,
          lastUpdate: new Date(),
        });
      }
      return {
        connection: {
          ...state.connection,
          subscriptions: newSubscriptions,
        },
      };
    }),

  recordSubscriptionReconnection: (entityType) =>
    set((state) => {
      const newSubscriptions = new Map(state.connection.subscriptions);
      const existing = newSubscriptions.get(entityType);
      if (existing) {
        const now = new Date();
        newSubscriptions.set(entityType, {
          ...existing,
          status: 'active',
          reconnections: existing.reconnections + 1,
          lastReconnectedAt: now,
          connectedAt: now,
          error: undefined,
        });
      }
      return {
        connection: {
          ...state.connection,
          subscriptions: newSubscriptions,
        },
      };
    }),

  recordSubscriptionStop: (entityType) =>
    set((state) => {
      const newSubscriptions = new Map(state.connection.subscriptions);
      const existing = newSubscriptions.get(entityType);
      if (existing) {
        newSubscriptions.set(entityType, {
          ...existing,
          status: 'stopped',
          stoppedCount: existing.stoppedCount + 1,
          lastStoppedAt: new Date(),
        });
      }
      return {
        connection: {
          ...state.connection,
          subscriptions: newSubscriptions,
        },
      };
    }),

  removeSubscription: (entityType) =>
    set((state) => {
      const newSubscriptions = new Map(state.connection.subscriptions);
      newSubscriptions.delete(entityType);
      return {
        connection: {
          ...state.connection,
          subscriptions: newSubscriptions,
        },
      };
    }),

  clearSubscriptions: () =>
    set((state) => ({
      connection: {
        ...state.connection,
        subscriptions: new Map(),
      },
    })),

  addConnectionError: (error) =>
    set((state) => {
      const newError: ConnectionError = {
        ...error,
        id: `err-${++errorIdCounter}`,
        timestamp: new Date(),
      };
      const errors = [newError, ...state.connection.recentErrors].slice(0, MAX_RECENT_ERRORS);
      return {
        connection: {
          ...state.connection,
          recentErrors: errors,
        },
      };
    }),

  clearConnectionErrors: () =>
    set((state) => ({
      connection: {
        ...state.connection,
        recentErrors: [],
      },
    })),
});
